import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { classNames } from "../../helpers/classNames";

const TextareaAutoSizeWithLimit = forwardRef(({
  label,
  labelClasses,
  inputValue = "",
  inputClasses,
  inputPlaceholder = "",
  inputName,
  isDisabled = false,
  error = false,
  onChange = () => { },
  errorType,
  errorText,
  isValidate,
  onBlur = () => { },
  currentHeight = '80px',
  maxLines = 2,
  minFontSize = 10,
  maxFontSize = 18,
  baseFontSize = 16,
}, ref) => {
  const buildInputClasses = classNames(
    "w-full rounded-md placeholder:text-md-dark bg-md-pink-light-2 border border-md-pink-light-1 px-3 text-md-dark !ring-0 !outline-0 focus:border-md-pink/40 transition-all duration-200 resize-none overflow-hidden leading-tight",
    error ? "border-red-300" : "",
    isDisabled
      ? "text-red-400 cursor-not-allowed bg-red-100 !border-red-200"
      : "",
    inputClasses
  );

  const [val, setVal] = useState(inputValue);
  const [fontSize, setFontSize] = useState(baseFontSize);
  const textareaRef = useRef(null);

  // Expose the focus method to parent components
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }));

  const measureTextHeight = (text, testFontSize) => {
    if (!textareaRef.current || !text) return 0;

    // First, count explicit line breaks (\n)
    const explicitLines = text.split('\n');
    let totalLines = 0;

    // Create a temporary canvas to measure text
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Get the computed font family from the textarea
    const computedStyle = window.getComputedStyle(textareaRef.current);
    ctx.font = `${testFontSize}px ${computedStyle.fontFamily}`;

    const textareaWidth = textareaRef.current.clientWidth - 24; // Account for padding

    // Check if text is centered
    const isTextCentered = computedStyle.textAlign === 'center' ||
      textareaRef.current.className.includes('text-center');

    // Measure each line created by explicit line breaks
    explicitLines.forEach(lineText => {
      if (lineText === '') {
        // Empty line still counts as one line
        totalLines += 1;
        return;
      }

      if (isTextCentered) {
        // For centered text, we need to be more conservative with line breaking
        // because words might not break exactly where we predict
        const words = lineText.split(' ');
        let linesForThisSegment = 1;
        let currentLine = '';

        for (let i = 0; i < words.length; i++) {
          const testLine = currentLine + words[i] + ' ';
          const metrics = ctx.measureText(testLine.trim());
          const testWidth = metrics.width;

          // Be more conservative for centered text (use 90% of available width)
          if (testWidth > textareaWidth * 0.9 && currentLine !== '') {
            linesForThisSegment++;
            currentLine = words[i] + ' ';
          } else {
            currentLine = testLine;
          }
        }

        totalLines += linesForThisSegment;
      } else {
        // Original logic for left-aligned text
        const words = lineText.split(' ');
        let linesForThisSegment = 1;
        let currentLine = '';

        for (let i = 0; i < words.length; i++) {
          const testLine = currentLine + words[i] + ' ';
          const metrics = ctx.measureText(testLine);
          const testWidth = metrics.width;

          if (testWidth > textareaWidth && currentLine !== '') {
            linesForThisSegment++;
            currentLine = words[i] + ' ';
          } else {
            currentLine = testLine;
          }
        }

        totalLines += linesForThisSegment;
      }
    });

    return totalLines;
  };

  const findOptimalFontSize = (text) => {
    if (!text.trim()) {
      return baseFontSize; // Use baseFontSize instead of maxFontSize for empty text
    }

    // Start from max font size and work down
    for (let testFontSize = maxFontSize; testFontSize >= minFontSize; testFontSize--) {
      const lines = measureTextHeight(text, testFontSize);
      if (lines <= maxLines) {
        return testFontSize;
      }
    }

    return minFontSize;
  };

  const handleKeyDown = (e) => {
    // Prevent Shift+Enter if it would exceed line limit
    if (e.key === 'Enter' && e.shiftKey) {
      const newValue = val + '\n';
      const lines = measureTextHeight(newValue, minFontSize);
      if (lines > maxLines) {
        e.preventDefault();
        return;
      }
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;

    // Check if new value would fit even at minimum font size
    if (newValue.length > val.length) {
      const lines = measureTextHeight(newValue, minFontSize);
      if (lines > maxLines) {
        return; // Don't allow input that exceeds line limit
      }
    }

    setVal(newValue);
    onChange(e);
  };

  // Calculate textarea height based on content and font size
  const calculateHeight = () => {
    const lineHeight = fontSize * 1.2;
    const padding = 16; // top and bottom padding

    if (!val.trim()) {
      // For empty text, show single line height
      return lineHeight + padding;
    }

    // Calculate actual lines needed for current content
    const actualLines = measureTextHeight(val, fontSize);
    const neededLines = Math.min(actualLines, maxLines);

    return neededLines * lineHeight + padding;
  };

  useEffect(() => {
    setVal(inputValue);
  }, [inputValue]);

  useEffect(() => {
    if (textareaRef.current) {
      const optimalSize = findOptimalFontSize(val);
      setFontSize(optimalSize);
    }
  }, [val, maxLines, minFontSize, maxFontSize, baseFontSize]);

  // Recalculate on resize
  useEffect(() => {
    const handleResize = () => {
      if (textareaRef.current) {
        const optimalSize = findOptimalFontSize(val);
        setFontSize(optimalSize);
      }
    };

    const timeoutId = setTimeout(handleResize, 100); // Debounce
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [val]);

  return (
    <>
      <div className="relative space-y-1 w-full">
        {label && (
          <div
            className={classNames(
              "text-xs font-medium text-shades-07 font-Poppins mb-1",
              labelClasses
            )}
          >
            {label}
            {isValidate && (
              <span className="text-[8px] text-red-500 ml-0.5 absolute top-[-2px]">
                <i className="fa-solid fa-asterisk"></i>
              </span>
            )}
          </div>
        )}
        <div className="relative flex">
          <textarea
            disabled={isDisabled}
            ref={textareaRef}
            value={val}
            name={inputName}
            placeholder={inputPlaceholder}
            className={buildInputClasses}
            style={{
              fontSize: `${fontSize}px`,
              lineHeight: '1.2',
              height: `${calculateHeight()}px`,
              paddingTop: '8px',
              paddingBottom: '8px'
            }}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
            rows={maxLines}
          />
        </div>

        {/* Debug info - remove in production */}
        {/* <div className="text-xs text-gray-500">
          Font: {fontSize}px | Lines: {measureTextHeight(val, fontSize)} | Max: {maxLines}
        </div> */}

        {errorType && errorType === "error" && (
          <div className="text-xs text-red-600 mt-1">
            <i className="fa-regular fa-fw fa-square-exclamation text-sm mr-1"></i>
            {errorText}
          </div>
        )}
        {errorType && errorType === "warning" && (
          <div className="text-xs text-amber-600 mt-1">
            <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
            {errorText}
          </div>
        )}
        {errorType && errorType === "success" && (
          <div className="text-xs text-green-600 mt-1">
            <i className="fa-regular fa-fw fa-circle-check text-sm mr-1"></i>
            {errorText}
          </div>
        )}
        {errorType && errorType === "info" && (
          <div className="text-xs text-sky-600 mt-1">
            <i className="fa-regular fa-fw fa-circle-info text-sm mr-1"></i>
            {errorText}
          </div>
        )}
      </div>
    </>
  );
});

TextareaAutoSizeWithLimit.displayName = 'TextareaAutoSizeWithLimit';

export default TextareaAutoSizeWithLimit;